import { FEATURE_MANIFESTS } from '../feature-manifest';
import { ONBOARDING_REGISTRY } from '../onboarding/onboarding-registry';
import type { FeatureCore } from './types';

/**
 * Index unifié `id → FeatureCore` — **l'unique surface** lue par la description
 * de tool MCP (Pulse). Fusionne deux sources :
 *
 * - les `FeatureManifest` (couverture doc large, filmable) ;
 * - la surface MCP des `OnboardingTask` qui portent un `mcp_feature` (tâches
 *   granulaires pas encore promues en manifeste).
 *
 * Remplace l'ancien `FEATURE_MANIFEST_BY_ID.get(id) ?? ONBOARDING_TASK_BY_ID
 * .get(id)?.mcp_feature` du service MCP (l'« aveu » d'archi). L'ordre
 * d'insertion préserve la même priorité : les tâches d'onboarding sont insérées
 * **en premier**, les manifestes **ensuite** — un `id` présent dans les deux est
 * donc résolu en faveur du manifeste (couverture plus riche), `Map` gardant la
 * dernière valeur. Cf. docs/feature-manifest-onboarding-unification.md § Niveau 3.
 *
 * Au fil de l'écriture des manifestes (Niveau 4), chaque tâche promue perd son
 * `mcp_feature` : l'entrée onboarding disparaît d'ici naturellement, sans
 * changement de code.
 */
export const FEATURE_CORE_BY_ID: ReadonlyMap<string, FeatureCore> = new Map<string, FeatureCore>([
  // Onboarding d'abord (priorité la plus basse en cas de doublon d'id).
  ...ONBOARDING_REGISTRY.tasks.flatMap((task): [string, FeatureCore][] => {
    const mcp = task.mcp_feature;
    if (!mcp) {
      return [];
    }
    return [
      [
        task.id,
        {
          id: task.id,
          intent: mcp.intent,
          required_scopes: task.required_scopes,
          prerequisites: mcp.prerequisites,
          gotchas: mcp.gotchas,
        },
      ],
    ];
  }),
  // Manifestes ensuite : écrasent une éventuelle entrée onboarding de même id.
  ...FEATURE_MANIFESTS.map((manifest): [string, FeatureCore] => [
    manifest.id,
    {
      id: manifest.id,
      intent: manifest.intent,
      required_scopes: manifest.required_scopes,
      prerequisites: manifest.prerequisites,
      gotchas: manifest.gotchas,
    },
  ]),
]);
